import { BaseService } from '../../../../../core/services/base.service';
import {
  Candidate,
  CandidateId,
  PersonalInfoExtractionResponse,
  ProfileUrlVerification,
} from '../../candidate/models/candidates.model';
import { JobApplication } from '../../job-application/models/job-application.model';

export class CandidatesService {
  static async GetCandidates(
    companyId: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
      search?: string;
    },
    CallbackCatch?: (error: Error) => void
  ): Promise<{ content: CandidateId[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';

    const result = CallbackCatch
      ? await BaseService.Get(
          `/companies/${companyId}/candidates${query}`,
          CallbackCatch,
          false
        )
      : await BaseService.Get(`/companies/${companyId}/candidates${query}`);

    if (!result) {
      return { content: [], totalElements: 0 };
    }

    return {
      content: Array.isArray(result.content) ? result.content : [],
      totalElements: result.totalElements ?? 0,
    };
  }

  static async GetById(candidateId: string): Promise<CandidateId> {
    return await BaseService.Get(`/candidates/${candidateId}`);
  }

  static async VerifyProfileUrl(url: string): Promise<ProfileUrlVerification> {
    const rethrow = (error: Error) => {
      throw error;
    };
    return await BaseService.Post(
      '/candidates/profile-url/verify',
      { url },
      rethrow,
      false
    );
  }

  static async Post(
    candidate: Partial<Candidate>,
    companyId: string
  ): Promise<CandidateId> {
    const payload = {
      firstname: candidate.firstname,
      lastname: candidate.lastname,
      email: candidate.email,
      phoneNumber: candidate.phoneNumber,
      profileURL: candidate.profileURL,
      companyId: companyId,
      active: candidate.active,
    };

    const customErrorCallback = (error: Error) => {
      throw error;
    };

    return await BaseService.Post('/candidates', payload, customErrorCallback);
  }

  static async Patch(
    candidate: Partial<Candidate>,
    candidateId: string,
    companyId: string
  ): Promise<Candidate> {
    return await BaseService.Put(`/candidates/${candidateId}`, {
      firstname: candidate.firstname,
      lastname: candidate.lastname,
      email: candidate.email,
      phoneNumber: candidate.phoneNumber,
      profileURL: candidate.profileURL,
      companyId: companyId,
      active: candidate.active,
      blocked: candidate.blocked,
      rate: candidate.rate,
    });
  }

  static async Delete(candidateId: string): Promise<CandidateId[]> {
    return await BaseService.Delete(`/candidates/${candidateId}`);
  }
  static async Block(
    candidate: Candidate,
    candidateId: string,
    companyId: string
  ): Promise<Candidate> {
    return await BaseService.Put(`/candidates/${candidateId}`, {
      firstname: candidate.firstname,
      lastname: candidate.lastname,
      email: candidate.email,
      phoneNumber: candidate.phoneNumber,
      profileURL: candidate.profileURL,
      companyId: companyId,
      active: candidate.active,
      blocked: true,
    });
  }

  static async Unblock(
    candidate: Candidate,
    candidateId: string,
    companyId: string
  ): Promise<Candidate> {
    return await BaseService.Put(`/candidates/${candidateId}`, {
      firstname: candidate.firstname,
      lastname: candidate.lastname,
      email: candidate.email,
      phoneNumber: candidate.phoneNumber,
      profileURL: candidate.profileURL,
      companyId: companyId,
      active: candidate.active,
      blocked: false,
    });
  }

  static async GetApplicationsByCandidate(
    candidateId: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
    }
  ): Promise<{ content: JobApplication[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';

    return await BaseService.Get(
      `/candidates/${candidateId}/job-applications${query}`
    );
  }

  static async ExportSelected(candidateIds: string[]): Promise<Blob> {
    return await BaseService.Blob(
      'POST',
      '/candidates/export',
      { candidateIds },
      undefined,
      false
    );
  }

  static async PersonalInfoExtraction(
    file: File
  ): Promise<PersonalInfoExtractionResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await BaseService.Post(
        '/public/resumes/personal-info-extraction',
        formData,
        undefined,
        false
      );
      return response;
    } catch (error) {
      console.error('Error extracting personal info:', error);
      throw error;
    }
  }
}
